/**
 * Cart Drawer DOM Updater
 * Provides utilities to update the cart drawer DOM with fresh cart data
 */

class CartDrawerDOMUpdater {
  constructor() {
    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartDrawerInner = document.querySelector('#CartDrawer');
  }

  /**
   * Fetches the latest cart data from Shopify
   * @returns {Promise<Object>} Cart data object
   */
  async fetchCartData() {
    try {
      // eslint-disable-next-line no-undef
      const response = await fetch('/cart.js', fetchConfig('javascript'));
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart data:', error);
      throw error;
    }
  }

  /**
   * Fetches the cart drawer HTML section
   * @returns {Promise<string>} HTML content
   */
  async fetchCartDrawerHTML() {
    try {
      // eslint-disable-next-line no-undef
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      return await response.text();
    } catch (error) {
      console.error('Error fetching cart drawer HTML:', error);
      throw error;
    }
  }

  /**
   * Updates the entire cart drawer DOM
   * @param {boolean} shouldOpen - Whether to open the drawer after update
   * @returns {Promise<void>}
   */
  async updateCartDrawer(shouldOpen = false) {
    try {
      const html = await this.fetchCartDrawerHTML();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get the new cart drawer content
      const newCartDrawer = doc.querySelector('cart-drawer');
      
      if (newCartDrawer && this.cartDrawer) {
        // Replace the entire cart drawer
        this.cartDrawer.innerHTML = newCartDrawer.innerHTML;
        
        // Publish cart update event
        const cartData = await this.fetchCartData();
        // eslint-disable-next-line no-undef
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-dom-updater',
          cartData: cartData,
        });
        
        // Open drawer if requested
        if (shouldOpen) {
          this.cartDrawer.open();
        }
      }
    } catch (error) {
      console.error('Error updating cart drawer:', error);
      throw error;
    }
  }

  /**
   * Updates specific sections within the cart drawer
   * @param {Array<string>} selectors - CSS selectors to update
   * @returns {Promise<void>}
   */
  async updateCartDrawerSections(selectors = ['.drawer__inner', '.cart-drawer__footer']) {
    try {
      const html = await this.fetchCartDrawerHTML();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      selectors.forEach((selector) => {
        const currentElement = document.querySelector(selector);
        const newElement = doc.querySelector(selector);
        
        if (currentElement && newElement) {
          currentElement.replaceWith(newElement);
        }
      });
      
      // Publish cart update event
      const cartData = await this.fetchCartData();
      // eslint-disable-next-line no-undef
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-dom-updater',
        cartData: cartData,
      });
    } catch (error) {
      console.error('Error updating cart drawer sections:', error);
      throw error;
    }
  }

  /**
   * Updates only the cart items section
   * @returns {Promise<void>}
   */
  async updateCartItems() {
    return this.updateCartDrawerSections(['cart-drawer-items', '.cart-drawer__footer']);
  }

  /**
   * Updates cart item count bubble
   * @returns {Promise<void>}
   */
  async updateCartBubble() {
    try {
      // eslint-disable-next-line no-undef
      const response = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const currentBubble = document.querySelector('.shopify-section[id*="cart-icon-bubble"]');
      const newBubble = doc.querySelector('.shopify-section');
      
      if (currentBubble && newBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    } catch (error) {
      console.error('Error updating cart bubble:', error);
      throw error;
    }
  }

  /**
   * Refreshes the entire cart drawer (items + footer + bubble)
   * @param {boolean} shouldOpen - Whether to open the drawer after refresh
   * @returns {Promise<void>}
   */
  async refreshAll(shouldOpen = false) {
    try {
      await Promise.all([
        this.updateCartDrawer(shouldOpen),
        this.updateCartBubble(),
      ]);
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
      throw error;
    }
  }

  /**
   * Adds a product to cart and updates the drawer
   * @param {number} variantId - Product variant ID
   * @param {number} quantity - Quantity to add
   * @param {Object} properties - Product properties (optional)
   * @returns {Promise<Object>} Cart response
   */
  async addToCart(variantId, quantity = 1, properties = {}) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      Object.entries(properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
      
      // eslint-disable-next-line no-undef
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;
      
      // eslint-disable-next-line no-undef
      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();
      
      if (data.status) {
        throw new Error(data.description || 'Failed to add to cart');
      }
      
      // Update drawer
      await this.refreshAll(true);
      
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Updates a line item quantity in the cart
   * @param {number} line - Line item index (1-based)
   * @param {number} quantity - New quantity
   * @returns {Promise<Object>} Cart response
   */
  async updateLineItem(line, quantity) {
    try {
      const body = JSON.stringify({
        line,
        quantity,
      });
      
      // eslint-disable-next-line no-undef
      const response = await fetch(`${routes.cart_change_url}`, {
        // eslint-disable-next-line no-undef
        ...fetchConfig(),
        body,
      });
      
      const data = await response.json();
      
      // Update drawer
      await this.refreshAll();
      
      return data;
    } catch (error) {
      console.error('Error updating line item:', error);
      throw error;
    }
  }

  /**
   * Removes a line item from the cart
   * @param {number} line - Line item index (1-based)
   * @returns {Promise<Object>} Cart response
   */
  async removeLineItem(line) {
    return this.updateLineItem(line, 0);
  }

  /**
   * Clears the entire cart
   * @returns {Promise<Object>} Cart response
   */
  async clearCart() {
    try {
      // eslint-disable-next-line no-undef
      const response = await fetch(`${routes.cart_clear_url}`, {
        // eslint-disable-next-line no-undef
        ...fetchConfig(),
        method: 'POST',
      });
      
      const data = await response.json();
      
      // Update drawer
      await this.refreshAll();
      
      return data;
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }
}

// Create global instance
window.cartDrawerDOMUpdater = new CartDrawerDOMUpdater();
